import { useState, useEffect, useContext } from 'react';
import { UserContext } from '~contexts/user';
import LocationContext from '../../locationContext';
import { useAuth0 } from '@auth0/auth0-react';
import { usePersistedFilterState } from '~components/listing/useFilterPersistence';

export const useFilters = ({ cacheKeyParts = [], persistFilters = true } = {}) => {
  const { isLoadingLocation, pricingDetails } = useContext(LocationContext);
  const { user, isLoaded: isLoadedUser } = useContext(UserContext);
  const { isLoading: isLoadingAuthentication } = useAuth0();
  const { operations } = user;

  const defaultFilters = {
    price: null,
    maxDistance: operations?.maxTravelDistance || null,
    languages: operations?.languages || [],
    days: operations?.timeframes || [],
    attributes: operations?.blockers || [],
  };

  const { filters, setFilters, touched, setTouched, wasRestored } = usePersistedFilterState({
    cacheKeyParts,
    defaultFilters,
    enabled: persistFilters,
  });
  const [isInitializedLocation, setIsInitializedLocation] = useState(false);
  const [isInitializedOperations, setIsInitializedOperations] = useState(false);


  const updateFilters = (newFilters) => {
    setTouched(true);
    setFilters(newFilters);
  };

  const resetFilters = () => {
    setTouched(true);
    setFilters(defaultFilters);
  };

  useEffect(() => {
    if (isLoadingLocation || !isInitializedOperations) {
      return;
    }

    // Restored selections keep their price; only an out-of-range value is re-defaulted.
    const outOfRange = pricingDetails && filters.price !== null &&
        (pricingDetails.minPrice > filters.price || pricingDetails.maxPrice < filters.price);

    if ((!wasRestored() && (!isInitializedLocation || !pricingDetails)) || outOfRange) {
      setFilters({
        ...filters,
        price: operations?.minimumRate.amount || pricingDetails?.minPrice,
      });
    }

    setIsInitializedLocation(true);
  }, [isLoadingLocation, isInitializedOperations, pricingDetails]);

  useEffect(() => {
    if(isLoadingAuthentication || !isLoadedUser) {
      return;
    }

    if(!operations) {
      setIsInitializedOperations(true);
      return;
    }

    // Restored selections win over the operations-derived defaults.
    if (wasRestored()) {
      setIsInitializedOperations(true);
      return;
    }

    setFilters({
      ...filters,
      languages: operations?.languages || [],
      days: operations?.timeframes || [],
      attributes: operations?.blockers || [],
    });
    setIsInitializedOperations(true);
  }, [operations, isLoadedUser]);

  return { filters, updateFilters, resetFilters, areFiltersInitialized: isInitializedLocation && isInitializedOperations, filtersTouched: touched };
};
